import React, { useState, useRef, useEffect, useContext } from "react";
import MyNavbar from "../../../components/customer/MyNavbar";
import "../../../assets/css/customer/venues/BookingDetails.css";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { FaSearchPlus } from "react-icons/fa";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const PaymentForm = ({ bookingId, advanceAmount, baseUrl, onPaymentSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [payLoading, setPayLoading] = useState(false);
  const [cardError, setCardError] = useState("");

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setPayLoading(true);
    setCardError("");

    try {
      const intentRes = await axios.post(`${baseUrl}/customer/payments/create-intent`, {
        booking_id: bookingId,
      });

      if (!intentRes.data.success) {
        setCardError("Could not create the payment intent. Please try again.");
        setPayLoading(false);
        return;
      }

      const clientSecret = intentRes.data.clientSecret;
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (result.error) {
        setCardError(result.error.message);
        setPayLoading(false);
        return;
      }

      if (result.paymentIntent.status === "succeeded") {
        await axios.post(`${baseUrl}/customer/payments/confirm`, { booking_id: bookingId });
        onPaymentSuccess();
      }
    } catch (err) {
      console.log(err);
      setCardError("Something went wrong. Please try again.");
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <div className="bd-payment-form">
      <p style={{ marginBottom: "10px", fontWeight: "600" }}>
        Advance Payment (10%): PKR {Number(advanceAmount).toLocaleString("en-PK")}
      </p>
      <div style={{ border: "1px solid #d1d5db", borderRadius: "8px", padding: "12px", marginBottom: "12px" }}>
        <CardElement options={{ style: { base: { fontSize: "16px" } }, hidePostalCode: true }} />
      </div>
      {cardError && <p style={{ color: "red", fontSize: "13px" }}>{cardError}</p>}
      <button className="bd-confirm-btn" disabled={!stripe || payLoading} onClick={handlePay}>
        {payLoading ? "Processing..." : `Pay PKR ${Number(advanceAmount).toLocaleString("en-PK")} Advance`}
      </button>
      <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "8px" }}>
        Test card: 4242 4242 4242 4242, any future date, any CVC
      </p>
    </div>
  );
};

// ── Calendar Component ──
const AvailabilityCalendar = ({ bookedDates, selectedDate, onDateSelect }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const monthNames = ["January","February","March","April","May","June",
    "July","August","September","October","November","December"];
  const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const formatDateStr = (year, month, day) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="bd-calendar">
      <div className="bd-calendar-header">
        <button className="bd-cal-nav" onClick={prevMonth}>&#8249;</button>
        <span className="bd-cal-title">{monthNames[viewMonth]} {viewYear}</span>
        <button className="bd-cal-nav" onClick={nextMonth}>&#8250;</button>
      </div>
      <div className="bd-cal-days-row">
        {dayNames.map(d => <span key={d} className="bd-cal-day-name">{d}</span>)}
      </div>
      <div className="bd-cal-grid">
        {cells.map((day, i) => {
          if (!day) return <span key={`empty-${i}`} className="bd-cal-cell bd-cal-cell--empty" />;

          const dateStr = formatDateStr(viewYear, viewMonth, day);
          const isBooked = bookedDates.includes(dateStr);
          const isSelected = selectedDate === dateStr;
          const isPast = new Date(dateStr) < new Date(today.toDateString());

          let cellClass = "bd-cal-cell";
          if (isPast) cellClass += " bd-cal-cell--past";
          else if (isBooked) cellClass += " bd-cal-cell--booked";
          else if (isSelected) cellClass += " bd-cal-cell--selected";
          else cellClass += " bd-cal-cell--available";

          return (
            <span
              key={dateStr}
              className={cellClass}
              onClick={() => !isBooked && !isPast && onDateSelect(dateStr)}
              title={isBooked ? "Already booked" : isPast ? "Past date" : "Available"}
            >
              {day}
            </span>
          );
        })}
      </div>
      <div className="bd-cal-legend">
        <span><span className="bd-legend-dot bd-legend-dot--available" /> Available</span>
        <span><span className="bd-legend-dot bd-legend-dot--booked" /> Booked</span>
        <span><span className="bd-legend-dot bd-legend-dot--selected" /> Selected</span>
        <span><span className="bd-legend-dot bd-legend-dot--past" /> Past</span>
      </div>
    </div>
  );
};

// Event type options — supervisor to confirm whether this exact list is fine or needs updating
const EVENT_TYPES = ["Wedding", "Mehndi", "Barat", "Walima", "Engagement", "Other"];

const BookingDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { baseUrl } = useContext(AppContext);
  const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));

  const venue = location.state?.venue;
  const gallery = venue?.gallery ? JSON.parse(venue.gallery) : [];
  const venueImages = gallery.length > 0
    ? gallery.map((url, i) => ({ id: i, url, caption: venue.venueName }))
    : [{ id: 0, url: venue?.coverImage, caption: venue?.venueName }];

  const [eventType, setEventType] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [guestCount, setGuestCount] = useState("");
  const [showSummary, setShowSummary] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [showPayment, setShowPayment] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [advanceAmount, setAdvanceAmount] = useState(0);

  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isVenueAvailable, setIsVenueAvailable] = useState(true);
  const [bookedDates, setBookedDates] = useState([]);

  const autoRef = useRef(null);

  // ── Price calculations ──
  const rawPrice = Number(String(venue?.priceRange || 0).replace(/,/g, ''));
  const advancePercent = 10;
  const platformCommission = 5;
  const hallBooking = 5;
  const advancePayable = Math.round(rawPrice * advancePercent / 100);
  const platformAmount = Math.round(rawPrice * platformCommission / 100);
  const hallAmount = Math.round(rawPrice * hallBooking / 100);

  // ── Guest count vs hall capacity validation ──
  const venueCapacity = Number(venue?.capacity || 0);
  const guestCountNum = Number(guestCount || 0);
  const guestCountEntered = guestCount !== "" && guestCountNum > 0;
  const isGuestCountValid = guestCountEntered && (venueCapacity === 0 || guestCountNum <= venueCapacity);

  const fetchBookedDates = () => {
    if (!venue?.id) return;
    const formData = new FormData();
    formData.append('wevenue_id', venue.id);
    axios.post(`${baseUrl}/customer/bookings/getBookedDates`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => { if (res.data.success) setBookedDates(res.data.bookedDates); })
    .catch((err) => console.log(err));
  };

  const checkAvailability = () => {
    if (!venue?.id || !eventDate) { setIsVenueAvailable(true); return; }
    setCheckingAvailability(true);
    const formData = new FormData();
    formData.append('wevenue_id', venue.id);
    formData.append('date', eventDate);
    axios.post(`${baseUrl}/customer/bookings/checkAvailability`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => { if (res.data.success) setIsVenueAvailable(res.data.available); })
    .catch((err) => console.log(err))
    .finally(() => setCheckingAvailability(false));
  };

  useEffect(() => { checkAvailability(); }, [eventDate, venue?.id]);
  useEffect(() => { fetchBookedDates(); }, []);

  useEffect(() => {
    autoRef.current = setInterval(() =>
      setCurrentSlide(p => (p + 1) % venueImages.length), 4000);
    return () => clearInterval(autoRef.current);
  }, []);

  const goTo = (i) => {
    clearInterval(autoRef.current);
    setCurrentSlide(i);
    autoRef.current = setInterval(() =>
      setCurrentSlide(p => (p + 1) % venueImages.length), 4000);
  };

  const prevSlide = () => goTo((currentSlide - 1 + venueImages.length) % venueImages.length);
  const nextSlide = () => goTo((currentSlide + 1) % venueImages.length);

  // ── FIXED: date-only strings ("YYYY-MM-DD") are parsed manually here
  // instead of via `new Date(dateStr)`, which JS treats as UTC midnight and
  // can shift the displayed day backward depending on the browser's timezone. ──
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const [year, month, day] = dateStr.split('-').map(Number);
    const monthNames = ["January","February","March","April","May","June",
      "July","August","September","October","November","December"];
    return `${String(day).padStart(2, '0')} ${monthNames[month - 1]} ${year}`;
  };

  const formatAmount = (val) => {
    if (!val) return "—";
    return "PKR " + Number(val).toLocaleString("en-PK");
  };

  const handleSubmitBooking = () => {
    setSubmitLoading(true);
    const formData = new FormData();
    formData.append('wecustomer_id', loggedInCustomer.id);
    formData.append('wevenue_id', venue.id);
    formData.append('date', eventDate);
    formData.append('eventType', eventType);
    formData.append('guestCount', guestCount);
    // NOTE: weevent_id has been removed because the My Event screen is being removed.
    // Once the supervisor confirms whether the backend will still create a Weevent record
    // or store eventType/guestCount directly as Webooking table columns, this formData
    // and CustomerBookingController@createBooking will both need to be updated accordingly.

    axios.post(`${baseUrl}/customer/bookings/createBooking`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => {
      if (res.data.success === true) {
        setShowSummary(false);
        setCreatedBookingId(res.data.booking.id);
        setTotalAmount(res.data.booking.totalAmount);
        setAdvanceAmount(res.data.booking.advanceAmount);
        setShowPayment(true);
        fetchBookedDates();
      } else {
        setShowSummary(false);
        setIsVenueAvailable(false);
        Swal.fire({
          icon: 'error',
          title: 'Not Available',
          text: res.data.message || 'This venue is not available for this date.',
          confirmButtonColor: '#7c3aed',
        });
      }
    })
    .catch((err) => console.log(err))
    .finally(() => setSubmitLoading(false));
  };

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    Swal.fire({
      icon: 'success',
      title: 'Payment Successful!',
      text: 'Advance payment received. Booking request submitted.',
      confirmButtonColor: '#7c3aed',
      timer: 2500,
      showConfirmButton: false
    });
    navigate("/customer/my-bookings");
  };

  const canConfirm = eventType && eventDate && guestCountEntered && isGuestCountValid && isVenueAvailable && !checkingAvailability;

  if (!venue) {
    return (
      <>
        <MyNavbar />
        <div className="bd-wrapper">
          <p style={{ padding: "40px", textAlign: "center" }}>
            No venue selected. Please go back and choose a venue.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <MyNavbar />
      <div className="bd-wrapper">

        <div className="bd-header">
          <div className="bd-header-inner">
            <button className="bd-back-btn" onClick={() => window.history.back()}>← Back to Venue</button>
            <div className="bd-header-text">
              <span className="bd-badge">Booking Customization</span>
              <h1 className="bd-title">{venue.venueName}</h1>
              <p className="bd-subtitle">
                📍 {venue.location}, {venue.city} &nbsp;|&nbsp; 👥 Capacity: {venue.capacity} guests
              </p>
            </div>
          </div>
        </div>

        <div className="bd-carousel-wrap">
          <div className="bd-carousel">
            {venueImages.map((img, i) => (
              <div key={i} className={`bd-carousel-slide ${i === currentSlide ? "bd-carousel-slide--active" : ""}`}>
                <img src={img.url} alt={img.caption} className="bd-carousel-img" />
                <span className="bd-carousel-zoom-icon">
                  <FaSearchPlus />
                </span>
                <div className="bd-carousel-caption">{img.caption}</div>
              </div>
            ))}
            <button className="bd-carousel-btn bd-carousel-btn--prev" onClick={prevSlide}>&#8249;</button>
            <button className="bd-carousel-btn bd-carousel-btn--next" onClick={nextSlide}>&#8250;</button>
            <div className="bd-carousel-counter">{currentSlide + 1} / {venueImages.length}</div>
          </div>
          <div className="bd-carousel-dots">
            {venueImages.map((_, i) => (
              <button key={i}
                className={`bd-carousel-dot ${i === currentSlide ? "bd-carousel-dot--active" : ""}`}
                onClick={() => goTo(i)} />
            ))}
          </div>
        </div>

        <div className="bd-main">
          <div className="bd-center-col">

            {/* Availability Calendar */}
            <div className="bd-card">
              <h3 className="bd-budget-title">📅 Venue Availability</h3>
              <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "12px" }}>
                Red dates are already booked. Click an available date to select it.
              </p>
              <AvailabilityCalendar
                bookedDates={bookedDates}
                selectedDate={eventDate}
                onDateSelect={(date) => setEventDate(date)}
              />
            </div>

            {/* Event Type + Guest Count */}
            <div className="bd-card">
              <h3 className="bd-budget-title">🎉 Event Details</h3>

              <div style={{ marginBottom: "14px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
                  Event Type
                </label>
                <select
                  className="form-select"
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                >
                  <option value="">Select event type</option>
                  {EVENT_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
                  Guest Count
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="How many guests are coming?"
                  value={guestCount}
                  min="1"
                  onChange={(e) => setGuestCount(e.target.value)}
                />

                {guestCountEntered && isGuestCountValid && (
                  <p style={{ fontSize: "13px", color: "#10b981", fontWeight: 600, marginTop: "8px" }}>
                    ✅ {venue.venueName} is suitable for your {guestCountNum} guests (Capacity: {venueCapacity}).
                  </p>
                )}
                {guestCountEntered && !isGuestCountValid && (
                  <p style={{ fontSize: "13px", color: "#dc2626", fontWeight: 600, marginTop: "8px" }}>
                    ⚠️ {venue.venueName}'s capacity is {venueCapacity} guests — not enough for this many guests. Please reduce the guest count or choose another venue.
                  </p>
                )}
              </div>
            </div>

            <div className="bd-card bd-budget-card">
              <h3 className="bd-budget-title">💰 Your Selection Summary</h3>

              {/* Event Info */}
              <div className="bd-budget-stat">
                <span>Event Type</span>
                <strong style={{ textTransform: 'capitalize' }}>{eventType || "—"}</strong>
              </div>
              <div className="bd-budget-stat">
                <span>Event Date</span>
                <strong>{eventDate ? formatDate(eventDate) : <span style={{ color: "#d1d5db" }}>Not set</span>}</strong>
              </div>
              <div className="bd-budget-stat">
                <span>Guest Count</span>
                <strong>{guestCountEntered ? guestCountNum : "—"}</strong>
              </div>
              <div className="bd-budget-stat">
                <span>Venue</span>
                <strong>{venue.venueName}</strong>
              </div>

              <div className="bd-budget-divider" />

              {/* Price Breakdown */}
              <div className="bd-budget-stat">
                <span>Total Hall Price</span>
                <strong>{formatAmount(rawPrice)}</strong>
              </div>

              <div className="bd-budget-stat">
                <span style={{ fontWeight: 600, color: '#7c3aed' }}>Advance Payable (10%)</span>
                <strong style={{ color: '#7c3aed' }}>{formatAmount(advancePayable)}</strong>
              </div>

              {/* Breakdown of 10% */}
              <div style={{
                background: '#f5f3ff',
                borderRadius: '8px',
                padding: '10px 14px',
                marginBottom: '8px',
                fontSize: '13px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: '#6b7280' }}>├── Platform Commission (5%)</span>
                  <span style={{ fontWeight: 600, color: '#10b981' }}>{formatAmount(platformAmount)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>└── Hall Booking (5%)</span>
                  <span style={{ fontWeight: 600, color: '#3b82f6' }}>{formatAmount(hallAmount)}</span>
                </div>
              </div>

              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                The remaining amount ({formatAmount(rawPrice - advancePayable)}) will be paid at the venue after the event.
              </div>

              <div className="bd-budget-divider" />

              {/* Services note */}
              <div style={{
                background: '#fef3c7',
                borderRadius: '8px',
                padding: '10px 14px',
                marginBottom: '12px',
                fontSize: '13px',
                color: '#92400e'
              }}>
                ℹ️ <strong>Note:</strong> This price includes only the hall, AC and parking.
                Catering, decoration and other services are not included.
              </div>

              {checkingAvailability && (
                <p style={{ fontSize: "13px", color: "#6b7280" }}>Checking venue availability...</p>
              )}
              {!checkingAvailability && eventDate && !isVenueAvailable && (
                <p style={{ fontSize: "13px", color: "#dc2626", fontWeight: 600 }}>
                  ⚠️ This venue is not available for this date. Please try another date.
                </p>
              )}

              <div className="bd-budget-divider" />

              {!canConfirm && (
                <p className="bd-empty-msg">
                  {!eventType
                    ? "Select an event type! 🎉"
                    : !eventDate
                      ? "Select a date from the calendar! 📅"
                      : !guestCountEntered
                        ? "Enter guest count! 👥"
                        : !isGuestCountValid
                          ? "Guest count exceeds venue capacity 🚫"
                          : !isVenueAvailable
                            ? "This venue can't be booked for this date 🚫"
                            : "Please complete the details!"}
                </p>
              )}

              <button
                className={`bd-confirm-btn ${!canConfirm ? "bd-confirm-btn--disabled" : ""}`}
                onClick={() => canConfirm && setShowSummary(true)}
              >
                Confirm Booking →
              </button>
              <button className="bd-enquiry-btn">Send Enquiry</button>
              <button className="bd-chat-btn" onClick={() => navigate("/customer/chat")}>
                💬 Chat with Admin
              </button>
            </div>
          </div>
        </div>

        {/* Summary Modal */}
        {showSummary && (
          <div className="bd-modal-overlay" onClick={() => setShowSummary(false)}>
            <div className="bd-modal" onClick={e => e.stopPropagation()}>
              <button className="bd-modal-close" onClick={() => setShowSummary(false)}>✕</button>
              <div className="bd-modal-header">
                <span className="bd-modal-icon">🎉</span>
                <h2>Booking Summary</h2>
                <p>{venue.venueName}</p>
              </div>
              <div className="bd-modal-body">
                <div className="bd-modal-row"><span>Event Type</span><strong style={{ textTransform: 'capitalize' }}>{eventType}</strong></div>
                <div className="bd-modal-row"><span>📅 Event Date</span><strong>{formatDate(eventDate)}</strong></div>
                <div className="bd-modal-row"><span>👥 Guest Count</span><strong>{guestCountNum}</strong></div>
                <div className="bd-modal-row"><span>🏛️ Venue</span><strong>{venue.venueName}</strong></div>
                <div className="bd-modal-row"><span>📍 Location</span><strong>{venue.location}, {venue.city}</strong></div>
                <div className="bd-modal-row"><span>💰 Total Hall Price</span><strong>{formatAmount(rawPrice)}</strong></div>
                <div className="bd-modal-row"><span>⚡ Advance Payable (10%)</span><strong style={{ color: '#7c3aed' }}>{formatAmount(advancePayable)}</strong></div>
                <div className="bd-modal-divider" />
                <div style={{ fontSize: '13px', background: '#f5f3ff', borderRadius: '8px', padding: '10px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ color: '#6b7280' }}>Platform Commission (5%)</span>
                    <span style={{ fontWeight: 600, color: '#10b981' }}>{formatAmount(platformAmount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Hall Booking (5%)</span>
                    <span style={{ fontWeight: 600, color: '#3b82f6' }}>{formatAmount(hallAmount)}</span>
                  </div>
                </div>
                <div className="bd-modal-divider" />
                <p style={{ fontSize: "13px", color: "#6b7280" }}>
                  The remaining amount ({formatAmount(rawPrice - advancePayable)}) will be paid at the venue after the event.
                </p>
                <p style={{ fontSize: "13px", color: "#92400e", background: '#fef3c7', borderRadius: '8px', padding: '8px 12px' }}>
                  ℹ️ Catering, decoration and other services are not included in this price.
                </p>
              </div>
              <div className="bd-modal-footer">
                <button className="bd-confirm-btn" disabled={submitLoading} onClick={handleSubmitBooking}>
                  {submitLoading ? (
                    <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Submitting...</>
                  ) : 'Submit Booking Request'}
                </button>
                <button className="bd-enquiry-btn" onClick={() => setShowSummary(false)}>Edit Selection</button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPayment && (
          <div className="bd-modal-overlay">
            <div className="bd-modal" onClick={e => e.stopPropagation()}>
              <div className="bd-modal-header">
                <span className="bd-modal-icon">💳</span>
                <h2>Pay Advance Amount</h2>
                <p>Total Hall Price: {formatAmount(totalAmount)} | Advance (10%): {formatAmount(advancePayable)}</p>
              </div>
              <div className="bd-modal-body">
                <Elements stripe={stripePromise}>
                  <PaymentForm
                    bookingId={createdBookingId}
                    advanceAmount={advanceAmount}
                    baseUrl={baseUrl}
                    onPaymentSuccess={handlePaymentSuccess}
                  />
                </Elements>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default BookingDetails;